export interface ApiError {
  message: string;
  fieldErrors?: Record<string, string>;
}

const ERROR_MESSAGES: Record<number, { fr: string; en: string }> = {
  400: {
    fr: 'La requête est invalide',
    en: 'The request is invalid',
  },
  401: {
    fr: 'Vous devez être connecté pour effectuer cette action',
    en: 'You must be logged in to perform this action',
  },
  403: {
    fr: "Vous n'avez pas l'autorisation d'accéder à cette ressource",
    en: 'You are not authorized to access this resource',
  },
  404: {
    fr: 'La ressource demandée est introuvable',
    en: 'The requested resource was not found',
  },
  422: {
    fr: 'Les données envoyées sont invalides',
    en: 'The submitted data is invalid',
  },
  429: {
    fr: 'Trop de tentatives. Veuillez patienter avant de réessayer',
    en: 'Too many attempts. Please wait before trying again',
  },
  500: {
    fr: 'Une erreur serveur est survenue. Veuillez réessayer plus tard',
    en: 'A server error occurred. Please try again later',
  },
};

function getLang(): 'fr' | 'en' {
  try {
    const stored = localStorage.getItem('lodiss_lang');
    if (stored === 'en') return 'en';
  } catch {}
  return 'fr';
}

export function handleApiError(response: Response): ApiError {
  const lang = getLang();

  if (response.status >= 500) {
    return {
      message: ERROR_MESSAGES[500]?.[lang] || ERROR_MESSAGES[500].en,
    };
  }

  const statusMessage = ERROR_MESSAGES[response.status];
  if (statusMessage) {
    return {
      message: statusMessage[lang],
    };
  }

  return {
    message: lang === 'fr' ? 'Une erreur est survenue' : 'An error occurred',
  };
}

export function parseErrorResponse(response: Response, errorData: unknown): ApiError {
  const lang = getLang();

  if (errorData && typeof errorData === 'object') {
    const data = errorData as Record<string, unknown>;

    if (data.detail) {
      if (typeof data.detail === 'string') {
        return { message: data.detail };
      }

      if (Array.isArray(data.detail)) {
        const fieldErrors: Record<string, string> = {};
        data.detail.forEach((item: unknown, index: number) => {
          if (item && typeof item === 'object') {
            const itemObj = item as Record<string, unknown>;
            const msg = (itemObj.msg as string) || String(item);
            const loc = itemObj.loc as string[] | undefined;
            const field = loc && loc.length > 1 ? String(loc[loc.length - 1]) : `field_${index}`;
            fieldErrors[field] = msg;
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          return {
            message: ERROR_MESSAGES[422]?.[lang] || ERROR_MESSAGES[422].en,
            fieldErrors,
          };
        }
      }

      if (typeof data.detail === 'object' && data.detail !== null) {
        const fieldErrors: Record<string, string> = {};
        Object.entries(data.detail).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            fieldErrors[key] = value[0];
          } else if (value) {
            fieldErrors[key] = String(value);
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          return {
            message: ERROR_MESSAGES[422]?.[lang] || ERROR_MESSAGES[422].en,
            fieldErrors,
          };
        }
      }
    }

    if (data.message && typeof data.message === 'string') {
      return { message: data.message };
    }
  }

  return handleApiError(response);
}

export function createApiError(status: number, errorData?: unknown): ApiError {
  const response = {
    status,
    ok: status >= 200 && status < 300,
    json: () => Promise.resolve(errorData || {}),
  } as Response;

  return parseErrorResponse(response, errorData);
}
